/**
 * 集团停车运营数据口径（本地只读适配，不引用其他页面模块）。
 *
 * 数据源：/api/ebc-content/propertyScreen/getParkingManagement
 * 字段口径：
 * - 总收入：parkingLotRevenueList[].totalMoney（回退四类收入之和）
 * - 总车位：parkingLotCarTypeList[] 的 group + inner + month + store + visit 合计
 * - 月租车位：parkingLotCarTypeList[].monthCount
 * - 临停 / 月租续费 / 储值 / 内部：tempMoney / monthRenewMoney / storeChargeMoney / innerRenewMoney
 * - 总车流量、进场、出场：parkingLotList[].totalCount / enterCount / exitCount
 * - 车位周转率：出场车次 ÷ 接入总车位
 */

function num (value) {
  if (value == null || value === '' || typeof value === 'boolean') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function sum (values) {
  return values.reduce((total, value) => total + (num(value) || 0), 0)
}

function round2 (value) {
  return Number(Number(value).toFixed(2))
}

function normalizedName (value) {
  return String(value || '').replace(/\s+/g, '')
}

/** 管理方 ID 优先，其次项目名称。 */
export function resolveProject (item, projects = []) {
  if (!item || !Array.isArray(projects) || !projects.length) return null
  if (item.managerId != null) {
    const byManager = projects.find(project => String(project.id) === String(item.managerId))
    if (byManager) return byManager
  }
  const name = normalizedName(item.projectName || item.parkingLotName)
  if (!name) return null
  return projects.find(project => {
    const full = normalizedName(project.name)
    const short = normalizedName(project.shortName)
    return (full && (name.includes(full) || full.includes(name))) ||
      (short && (name.includes(short) || short.includes(name)))
  }) || null
}

const CAR_TYPE_KEYS = ['groupCount', 'innerCount', 'monthCount', 'storeCount', 'visitCount']

/** 五类车辆数合计作为“总车位”。 */
function carTypeSpaces (row) {
  if (!row) return { spaces: 0, monthSpaces: 0 }
  const hasCount = CAR_TYPE_KEYS.some(key => num(row[key]) != null)
  return {
    spaces: hasCount ? sum(CAR_TYPE_KEYS.map(key => row[key])) : 0,
    monthSpaces: num(row.monthCount) || 0
  }
}

function emptyResult () {
  return {
    state: 'empty',
    totals: { revenue: null, flow: null, enter: null, exit: null, spaces: null, turnover: null },
    rows: [],
    lots: [],
    revenueRows: []
  }
}

/**
 * @param {object} payload 接口原始返回（{code,data} 或 data 本身）
 * @param {{ projects?: Array }} options 当前可见项目
 */
export function normalizeParking (payload, { projects = [] } = {}) {
  const data = payload && payload.data ? payload.data : payload
  if (!data || typeof data !== 'object') return emptyResult()
  const hasAnyList = ['parkingLotList', 'parkingLotRevenueList', 'parkingLotCarTypeList']
    .some(key => Array.isArray(data[key]))
  if (!hasAnyList) return emptyResult()

  const scopeIds = new Set(projects.map(project => String(project.id)))
  const inScope = item => {
    const project = resolveProject(item, projects)
    return project && scopeIds.has(String(project.id)) ? project : null
  }

  // 车位与车类数量：按五类车辆数合计。
  const carTypeByProject = new Map()
  ;(Array.isArray(data.parkingLotCarTypeList) ? data.parkingLotCarTypeList : []).forEach(row => {
    const project = inScope(row)
    if (!project) return
    const id = String(project.id)
    const current = carTypeByProject.get(id) || { spaces: 0, monthSpaces: 0 }
    const next = carTypeSpaces(row)
    carTypeByProject.set(id, {
      spaces: current.spaces + next.spaces,
      monthSpaces: current.monthSpaces + next.monthSpaces
    })
  })

  // 收入：总收入与四类拆分。
  const revenueByProject = new Map()
  const revenueRows = []
  ;(Array.isArray(data.parkingLotRevenueList) ? data.parkingLotRevenueList : []).forEach(row => {
    const project = inScope(row)
    if (!project) return
    const id = String(project.id)
    const total = num(row.totalMoney)
    const temporary = num(row.tempMoney) || 0
    const monthly = num(row.monthRenewMoney) || 0
    const store = num(row.storeChargeMoney) || 0
    const inner = num(row.innerRenewMoney) || 0
    const fallback = round2(temporary + monthly + store + inner)
    const money = total == null ? fallback : round2(total)
    const current = revenueByProject.get(id) || { revenue: 0, temporary: 0, monthly: 0, store: 0, inner: 0 }
    revenueByProject.set(id, {
      revenue: round2(current.revenue + money),
      temporary: round2(current.temporary + temporary),
      monthly: round2(current.monthly + monthly),
      store: round2(current.store + store),
      inner: round2(current.inner + inner)
    })
    revenueRows.push({
      id: String(row.parkingLotId || id),
      name: String(row.parkingLotName || project.shortName).trim(),
      projectId: id,
      revenue: money,
      temporary,
      monthly,
      store,
      inner
    })
  })

  // 流量：按车场汇总到项目。
  const lots = []
  ;(Array.isArray(data.parkingLotList) ? data.parkingLotList : []).forEach(lot => {
    const project = inScope(lot)
    if (!project) return
    lots.push({
      id: String(lot.parkingLotId || ''),
      name: String(lot.parkingLotName || project.shortName).trim(),
      projectId: String(project.id),
      spaces: num(lot.totalPlaceCount),
      remaining: num(lot.remainPlaceCount),
      enter: num(lot.enterCount) || 0,
      exit: num(lot.exitCount) || 0,
      flow: num(lot.totalCount) || 0,
      revenue: num(lot.totalMoney) || 0,
      useRate: num(lot.useRate),
      bizType: num(lot.bizType)
    })
  })

  const rows = projects.map(project => {
    const id = String(project.id)
    const carType = carTypeByProject.get(id) || { spaces: 0, monthSpaces: 0 }
    const money = revenueByProject.get(id) || { revenue: 0, temporary: 0, monthly: 0, store: 0, inner: 0 }
    const projectLots = lots.filter(lot => lot.projectId === id)
    const exit = sum(projectLots.map(lot => lot.exit))
    const enter = sum(projectLots.map(lot => lot.enter))
    const flow = sum(projectLots.map(lot => lot.flow))
    const physical = projectLots.filter(lot => lot.spaces != null && lot.spaces > 0 && lot.remaining != null && lot.remaining >= 0 && lot.remaining <= lot.spaces)
    const physicalSpaces = physical.length ? sum(physical.map(lot => lot.spaces)) : null
    const physicalUsed = physicalSpaces ? physicalSpaces - sum(physical.map(lot => lot.remaining)) : null
    return {
      id,
      shortName: project.shortName,
      businessName: project.businessName,
      spaces: carType.spaces,
      monthSpaces: carType.monthSpaces,
      revenue: money.revenue,
      temporary: money.temporary,
      monthly: money.monthly,
      store: money.store,
      inner: money.inner,
      visits: flow,
      enter,
      exit,
      turnover: carType.spaces ? round2(exit / carType.spaces) : null,
      useRate: physicalSpaces ? round2(physicalUsed / physicalSpaces * 100) : null,
      hasParking: projectLots.length > 0 || carType.spaces > 0 || money.revenue > 0
    }
  }).sort((a, b) => (b.revenue - a.revenue) || String(a.shortName).localeCompare(String(b.shortName), 'zh-CN'))

  const totals = {
    revenue: round2(sum(rows.map(row => row.revenue))),
    flow: sum(rows.map(row => row.visits)),
    enter: sum(rows.map(row => row.enter)),
    exit: sum(rows.map(row => row.exit)),
    spaces: sum(rows.map(row => row.spaces)),
    turnover: null
  }
  totals.turnover = totals.spaces ? round2(totals.exit / totals.spaces) : null

  const hasData = rows.some(row => row.hasParking) || revenueRows.length > 0 || rows.some(row => row.spaces > 0)
  return {
    state: hasData ? 'ready' : 'empty',
    totals,
    rows,
    lots,
    revenueRows: revenueRows.sort((a, b) => b.revenue - a.revenue)
  }
}

/** 账单金额弹窗使用的停车预览行。 */
export function parkingPreviewRows (parking) {
  if (!parking || parking.state !== 'ready') return []
  return parking.rows
    .filter(row => row.revenue > 0 || row.hasParking)
    .map(row => ({
      id: row.id,
      name: row.shortName,
      temporary: row.temporary,
      monthly: row.monthly,
      store: row.store,
      inner: row.inner,
      total: row.revenue
    }))
}
